<script lang="ts" setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useInfiniteScroll } from '@vueuse/core'
import { useRoute, useRouter } from 'vue-router/composables'
import { historyCommonConfig } from './configs/common'
import { api } from '@/api'
import { useReadStore, useToastStore, useTrackValueStore, useUserStore } from '@/stores'
import { TRACK_CHANNEL_NAME_VALUE, getDramaPlayerUrl } from '@/constants'
import { dramaLocalEpisode, isVideoBridge, videoBridgeVideoUrl } from '@/utils/videoBridge'
import { genPlayTraceId } from '@/utils/playTraceId'
import { resolveLoadingCoverUrl, storeGameLoadingAssets } from '@/utils/gameLoadingAssets'
import { useElementClick } from '@/composables/tracks/useElementClick'

type HistoryBook = GetReadHistoryListResponse['data']['records'][number]
type LibraryBook = GetBookshelfListResponse['data']['records'][number]
type ShelfTab = 'favorites' | 'history'

const PAGE_SIZE = 20

const config = historyCommonConfig

const { $toast } = useToastStore()
const { elementClick } = useElementClick()
const readStore = useReadStore()
const userStore = useUserStore()

interface PagedState<T> {
  items: T[]
  page: number
  total: number
  fetching: boolean
  ending: boolean
  error: any
}

function createPagedState<T>(): PagedState<T> {
  return reactive({
    items: [],
    page: 1,
    total: 0,
    fetching: false,
    ending: false,
    error: null,
  }) as PagedState<T>
}

const favorites = createPagedState<LibraryBook>()
const history = createPagedState<HistoryBook>()

const activeTab = ref<ShelfTab>('favorites')

// 继续阅读大卡
const lastPlay = ref<GetReadLastPlayResponse['data'] | null>(null)
// /read/last/play 不返回 small_theater 与 landscapeCoverUrl，从历史/收藏列表按 contentId 兜底匹配同一本书
const lastPlayListHit = computed<any>(() => {
  const id = lastPlay.value?.contentId
  if (!id)
    return null
  return [...history.items, ...favorites.items].find(item => item.contentId === id) || null
})

// 大卡角标：两个列表都没命中则不展示角标，不报错
const lastPlaySmallTheater = computed(() => !!lastPlayListHit.value?.small_theater)

// 大卡横版封面：接口本身或列表兜底
const lastPlayLandscapeUrl = computed(() => {
  return lastPlay.value?.landscapeCoverUrl || lastPlayListHit.value?.landscapeCoverUrl || ''
})

// 大卡封面：优先横版封面，缺省回退竖版 contentCoverUrl
const lastPlayCoverUrl = computed(() => lastPlayLandscapeUrl.value || lastPlay.value?.contentCoverUrl || '')

// 大卡固定 3:2（与首页 banner 一致，须与 .hero-cover 的 aspect-ratio 保持一致）：
// 横版封面完整展示，竖版封面顶对齐裁切；封面组件占位比例与卡片比例一致，避免按默认竖版 133% 占位被放大裁切
const HERO_RATIO_W = 3
const HERO_RATIO_H = 2
const heroCoverHeightRate = HERO_RATIO_H / HERO_RATIO_W * 100

async function fetchLastPlay() {
  try {
    const { data } = await api.get<any, GetReadLastPlayResponse>('/read/last/play')
    if (data?.contentId)
      lastPlay.value = data
  }
  catch {
    lastPlay.value = null
  }
}

// 影游大卡：数据源为桥接列表第一条（一期单剧），未开始也展示（设计稿"书架 未开始"态）；
// 拉取失败或列表为空回退到小说继续阅读大卡
const dramaHero = ref<any>(null)
// 命中桥接的影游 contentId 集合：历史/收藏列表接口不下发 play_mode / video_entry，
// 列表里的影游条目只能按 contentId 与桥接列表比对识别
const dramaIds = ref<Set<string>>(new Set())
// 本地推算的观看进度（只到章节粒度），0 = 未开始
const dramaEpisode = ref(0)
const dramaStarted = computed(() => dramaEpisode.value > 0)

async function fetchDramaHero() {
  try {
    const { data } = await api.get<any, { data: any[] }>('/interactive/book/video-bridge/list')
    const bridged = (data ?? []).filter(item => isVideoBridge(item))
    dramaHero.value = bridged[0] ?? null
    dramaIds.value = new Set(bridged.map(item => String(item.contentId)))
  }
  catch {
    dramaHero.value = null
  }
}

/** 列表条目是否影游：自带桥接字段或 contentId 在桥接列表中 */
function isDramaItem(item: any): boolean {
  return isVideoBridge(item) || dramaIds.value.has(String(item?.contentId))
}

/** 影游展示集数：未开始按第 1 集，已开始封顶总集数 */
function dramaEpisodeNo(item: any): number {
  const total = item?.chapterNum || 0
  const ep = Math.max(dramaEpisode.value, 1)
  return total ? Math.min(ep, total) : ep
}

function dramaEpisodeLabel(item: any): string {
  return config.texts.episodeOf(dramaEpisodeNo(item), item?.chapterNum || 0)
}

/** 影游观看进度条（章节粒度）；未开始返回 0 不展示 */
function dramaProgress(item: any): number {
  if (!dramaStarted.value || !item?.chapterNum)
    return 0
  const percent = (dramaEpisodeNo(item) / item.chapterNum) * 100
  return Math.min(100, Math.max(6, Math.round(percent)))
}

/** 已有进度（Continue）带 resume=1，播放器直接进入续看集；未开始落播放器封面页 */
function gotoDrama(item: any, from: string) {
  elementClick(`drama_library_${from}`)
  window.location.href = getDramaPlayerUrl(item.contentId, 'history', dramaStarted.value)
}

async function fetchFavorites() {
  favorites.fetching = true
  try {
    const { data } = await api.get<any, GetBookshelfListResponse>('/bookshelf/list', {
      params: { pageIndex: favorites.page, pageSize: PAGE_SIZE },
    })
    const next = [...favorites.items, ...(data.records || [])]
    // 与小说项目保持一致：按最近阅读时间倒序
    next.sort((a, b) => {
      const ta = a.lastReadTime ? new Date(a.lastReadTime).getTime() : 0
      const tb = b.lastReadTime ? new Date(b.lastReadTime).getTime() : 0
      return tb - ta
    })
    favorites.items = next
    favorites.total = data.total || 0
    if (favorites.page >= (data.pages || 0))
      favorites.ending = true
    else
      favorites.page += 1
  }
  catch (e) {
    favorites.error = e
  }
  finally {
    favorites.fetching = false
  }
}

async function fetchHistory() {
  history.fetching = true
  try {
    const { data } = await api.get<any, GetReadHistoryListResponse>('/read/history/list', {
      params: { pageIndex: history.page, pageSize: PAGE_SIZE },
    })
    history.items = [...history.items, ...(data.records || [])]
    history.total = data.total || 0
    if (history.page >= (data.pages || 0))
      history.ending = true
    else
      history.page += 1
  }
  catch (e) {
    history.error = e
  }
  finally {
    history.fetching = false
  }
}

const currentState = computed(() => activeTab.value === 'favorites' ? favorites : history)

async function loadCurrent() {
  const state = currentState.value
  if (state.fetching || state.ending || state.error)
    return
  if (activeTab.value === 'favorites')
    await fetchFavorites()
  else
    await fetchHistory()
}

function switchTab(tab: ShelfTab) {
  if (activeTab.value === tab)
    return
  elementClick(tab === 'favorites' ? 'shelf_favorites' : 'shelf_history')
  activeTab.value = tab
  // 首次进入该页签时拉取
  const state = currentState.value
  if (!state.items.length && !state.fetching && !state.ending)
    loadCurrent()
}

const infiniteRef = ref<HTMLElement>()
useInfiniteScroll(infiniteRef, async () => {
  await loadCurrent()
}, { distance: 50 })

const route = useRoute()
const from = computed(() => {
  return route.query.from as string
})

const router = useRouter()

interface PlayableBook {
  contentId: string
  contentChapterId?: string
  contentCoverUrl?: string
  offlineFlag?: boolean
}

// 跳转前拉一次详情：loading 封面 / 动画与详情页进入游戏走同一规则（列表只有 3:4 的 contentCoverUrl）
let navigating = false
async function gotoChapter(item: PlayableBook, chlName: TRACK_CHANNEL_NAME_VALUE) {
  if (item.offlineFlag)
    return $toast(config.texts.bookRemoved)
  if (navigating)
    return
  navigating = true
  readStore.update({
    contentId: item.contentId,
    chapterId: item.contentChapterId || '',
  })
  const trackValueStore = useTrackValueStore()
  trackValueStore.set({ bookId: item.contentId, firstOrigin: 4, originName: 4, chlName })

  let coverUrl = item.contentCoverUrl || ''
  let loadingAnimationUrl = ''
  let contentName = ''
  try {
    const { data } = await api.get<any, GetContentGetcontentdetailResponse>(
      '/interactive/book/detail',
      { params: { contentId: item.contentId } },
    )
    coverUrl = resolveLoadingCoverUrl(data, userStore.memberId) || coverUrl
    loadingAnimationUrl = (data as any)?.loadingAnimationUrl || ''
    contentName = data?.contentName || ''
  }
  catch {
    // 详情失败退回列表封面，不阻断进入游戏
  }
  storeGameLoadingAssets(coverUrl, loadingAnimationUrl)

  const query: Record<string, string> = {
    contentId: item.contentId,
    playTraceId: genPlayTraceId(),
    coverUrl,
  }
  if (contentName)
    query.contentName = contentName
  router.push({ name: 'game', query }).finally(() => {
    navigating = false
  })
}

function playLast() {
  if (!lastPlay.value)
    return
  elementClick('continue_play')
  gotoChapter(lastPlay.value, TRACK_CHANNEL_NAME_VALUE.READ_HISTORY)
}

function gotoBook(item: HistoryBook | LibraryBook) {
  // 影游卡：直接整页跳转到独立播放器项目，不进 /game
  if (isDramaItem(item)) {
    gotoDrama(item, 'card')
    return
  }
  gotoChapter(item as PlayableBook, TRACK_CHANNEL_NAME_VALUE.READ_HISTORY)
}

// 书卡进度：全书进度 = (已读完章节数 + 当前章节内进度) / 总章节数
// currentNumber/maxNumber 为章内对话进度（WGSQ-476 新接口字段）；缺省退化为纯章节进度
function bookProgress(item: any): number {
  // 未玩过（无上次阅读章节记录）不展示进度条：收藏后接口也会返回 sequenceNo/chapterNum，
  // 仅凭它们无法区分是否玩过，需以 contentChapterId 为准
  if (!item.contentChapterId)
    return 0
  if (item.sequenceNo && item.chapterNum) {
    const chapterInner = item.maxNumber ? Math.min(1, (item.currentNumber || 0) / item.maxNumber) : 0
    const percent = ((item.sequenceNo - 1 + chapterInner) / item.chapterNum) * 100
    // 保底 6%，避免刚开局时进度条视觉上不可见
    return Math.min(100, Math.max(6, Math.round(percent)))
  }
  if (typeof item.readProgress === 'number')
    return Math.min(100, Math.max(0, item.readProgress))
  return 0
}

function chapterLabel(item: any): string {
  const no = item.sequenceNo || 1
  return config.texts.chapterOf(no, item.chapterNum)
}

onMounted(() => {
  dramaEpisode.value = dramaLocalEpisode()
  fetchDramaHero()
  fetchLastPlay()
  // 两个列表都先拉首页，保证页签计数初始即展示
  fetchFavorites()
  fetchHistory()
})
</script>

<template>
  <div ref="infiniteRef" class="pb-90px h-100dvh overflow-scroll bg-n-bg-global no-scrollbar">
    <!-- Tab 页：显示品牌 Header + 钻石入口 -->
    <div v-if="!from" class="brand-header sticky top-0 z-10 flex items-center justify-between px-15px bg-n-bg-global">
      <img src="/images/vowpath/home/logo.png" class="brand-logo" alt="Vowpath">
      <CoinEntry />
    </div>
    <!-- 二级页：显示返回导航 -->
    <div v-if="from">
      <BasicNavbar />
      <div class="h-5" />
    </div>

    <!-- 影游大卡（桥接列表有数据时优先，未开始也展示） -->
    <div v-if="dramaHero" class="px-15px mt-6px">
      <div class="drama-hero rounded-12px overflow-hidden">
        <div class="hero-card relative" @click="gotoDrama(dramaHero, 'hero')">
          <BizDramaVideo
            class="hero-cover w-full"
            :src="videoBridgeVideoUrl(dramaHero.video_entry)"
            :poster="dramaHero.landscapeCoverUrl || dramaHero.coverUrl || ''"
          />
          <span class="drama-badge drama-badge-hero">INTERACTIVE DRAMA</span>
          <img
            v-if="dramaStarted"
            src="/images/vowpath/home/icon-play-circle.png"
            class="hero-play-icon"
          >
          <!-- 观看进度条（章节粒度），未开始不展示 -->
          <div v-if="dramaProgress(dramaHero)" class="hero-progress">
            <div class="hero-progress-inner" :style="{ width: `${dramaProgress(dramaHero)}%` }" />
          </div>
        </div>
        <div class="flex items-center justify-between px-15px py-12px">
          <div class="min-w-0 pr-12px">
            <p class="text-16px font-bold text-n-text-1 truncate">
              {{ dramaHero.contentName }}
            </p>
            <p class="text-12px mt-4px text-n-text-2 truncate">
              {{ dramaEpisodeLabel(dramaHero) }}
            </p>
          </div>
          <div class="play-btn shrink-0" @click="gotoDrama(dramaHero, 'hero_btn')">
            <template v-if="dramaStarted">
              {{ config.texts.continueText }}
            </template>
            <template v-else>
              <i class="play-triangle" />{{ config.texts.play }}
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- 继续阅读大卡（无影游时回退） -->
    <div v-else-if="lastPlay" class="px-15px mt-6px">
      <div class="hero-card relative rounded-12px overflow-hidden" @click="playLast">
        <!-- key 绑定封面地址：BizBookCover 不监听 src 变化，列表兜底的横图晚于 /read/last/play 返回时需重建 -->
        <BizBookCover
          :id="`lastplay-${lastPlay.contentId}`"
          :key="lastPlayCoverUrl"
          :src="lastPlayCoverUrl"
          :height-rate="heroCoverHeightRate"
          :small-theater="lastPlaySmallTheater"
          :is-report-imp="false"
          class="hero-cover w-full"
        />
        <!-- 底部阅读进度条（WGSQ-476） -->
        <div v-if="bookProgress(lastPlay)" class="cover-progress">
          <div class="cover-progress-inner" :style="{ width: `${bookProgress(lastPlay)}%` }" />
        </div>
      </div>
      <div class="flex items-center justify-between mt-10px">
        <div class="min-w-0 pr-12px">
          <p class="text-16px font-bold text-n-text-1 truncate">
            {{ lastPlay.contentName }}
          </p>
          <p class="text-12px mt-4px text-n-text-2 truncate">
            {{ config.texts.chapterOf(lastPlay.sequenceNo || 1) }}
            <span v-if="lastPlay.contentChapterName"> · {{ lastPlay.contentChapterName }}</span>
          </p>
        </div>
        <div class="play-btn shrink-0" @click="playLast">
          {{ config.texts.play }}
        </div>
      </div>
    </div>

    <!-- Favorites / History 双页签 -->
    <div class="flex items-center px-15px mt-18px">
      <div class="shelf-tab" :class="{ active: activeTab === 'favorites' }" @click="switchTab('favorites')">
        <span>{{ config.texts.favoritesTab }}</span>
        <span class="tab-count">({{ favorites.total }})</span>
        <i v-if="activeTab === 'favorites'" class="tab-underline" />
      </div>
      <div class="tab-divider mx-16px" />
      <div class="shelf-tab" :class="{ active: activeTab === 'history' }" @click="switchTab('history')">
        <span>{{ config.texts.historyTab }}</span>
        <span class="tab-count">({{ history.total }})</span>
        <i v-if="activeTab === 'history'" class="tab-underline" />
      </div>
    </div>

    <!-- 书籍三列网格 -->
    <div class="px-15px mt-14px">
      <!-- 空态 -->
      <div v-if="!currentState.fetching && currentState.items.length === 0" class="pt-10vh w-full text-center flex flex-col justify-center">
        <div>
          <img class="mx-a w-100px" src="@/assets/cover-empty.png" alt="empty">
          <p class="mt-14px text-13px text-n-text-2">
            {{ config.texts.noData }}
          </p>
          <div class="mt-30px">
            <router-link
              :to="{ name: 'home' }" replace
              class="inline-block text-n-primary text-14px py-2 px-4 border border-solid border-n-primary rounded-full"
            >
              {{ config.texts.goToNovel }}
            </router-link>
          </div>
        </div>
      </div>

      <template v-else>
        <div class="grid grid-cols-3 gap-x-10px gap-y-16px">
          <div
            v-for="item in currentState.items"
            :key="item.contentId"
            class="cursor-pointer"
            @click="gotoBook(item)"
          >
            <div class="relative rounded-8px overflow-hidden">
              <BizBookCover
                :id="`shelf-${item.contentId}`"
                :src="item.contentCoverUrl || ''"
                :small-theater="!!item.small_theater"
                :is-report-imp="false"
                class="shelf-cover w-full"
              />
              <!-- 影游卡：角标 + 播放小图标 + 本地进度条（封面展示，不播视频） -->
              <template v-if="isDramaItem(item)">
                <span class="drama-badge drama-badge-card">INTERACTIVE DRAMA</span>
                <img src="/images/vowpath/home/icon-play-circle.png" class="card-play-icon">
                <div v-if="dramaProgress(item)" class="cover-progress">
                  <div class="cover-progress-inner" :style="{ width: `${dramaProgress(item)}%` }" />
                </div>
              </template>
              <!-- 小说底部进度条 -->
              <div v-else-if="bookProgress(item)" class="cover-progress">
                <div class="cover-progress-inner" :style="{ width: `${bookProgress(item)}%` }" />
              </div>
            </div>
            <p class="mt-8px text-13px font-500 line-clamp-2 text-n-text-1 leading-17px">
              {{ item.contentName }}
            </p>
            <p class="mt-4px text-11px text-n-text-2 truncate">
              {{ isDramaItem(item) ? dramaEpisodeLabel(item) : chapterLabel(item) }}
            </p>
          </div>
        </div>

        <div v-if="currentState.ending && currentState.items.length" class="py-10 flex space-x-2.5 items-center justify-center">
          <span class="text-13px text-n-text-2">{{ config.texts.noMore }}</span>
        </div>
        <div v-else-if="currentState.fetching" class="py-15px flex items-center justify-center space-x-4 text-13px text-n-text-2">
          <div class="animate-spin">
            <i-icon-park-outline-loading />
          </div>
          <p>{{ config.texts.loading }}</p>
        </div>
      </template>
    </div>

    <BasicTabbar v-if="!from" tab="history" />
  </div>
</template>

<style lang="scss" scoped>
.no-scrollbar {
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
}

.brand-header {
  height: 44px;
}

.brand-logo {
  height: 25px;
  width: auto;
}

/* 大卡媒体区固定 3:2（与脚本 HERO_RATIO 一致）：图片/视频封面都由该类约束高度，
   否则竖版视频会按自身高度撑开卡片；竖版封面顶对齐裁切，横版封面完整展示 */
.hero-cover {
  aspect-ratio: 3 / 2;
  object-fit: cover;

  :deep(img) {
    object-position: center top;
  }
}

/* 影游大卡：媒体 + 信息条包在同一圆角卡片内（设计稿"书架"两态） */
.drama-hero {
  background: rgba(255, 255, 255, 0.08);
  cursor: pointer;
}

/* INTERACTIVE DRAMA 角标（橙→粉渐变，取自蓝湖 Design Tokens） */
.drama-badge {
  position: absolute;
  left: 0;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  background: linear-gradient(270deg, rgba(255, 185, 80, 1) 0%, rgba(252, 76, 139, 1) 100%);
  color: #ffffff;
  font-weight: bold;
  white-space: nowrap;
}

/* 大卡角标：胶囊形 124x20，距边 12px */
.drama-badge-hero {
  left: 12px;
  top: 12px;
  height: 20px;
  padding: 0 10px;
  border-radius: 30px;
  font-size: 10px;
}

/* 列表卡角标：贴左上角，圆角 [8,0,8,0] 68x13 */
.drama-badge-card {
  height: 13px;
  padding: 0 6px;
  border-radius: 8px 0 8px 0;
  font-size: 7px;
}

.hero-play-icon {
  position: absolute;
  left: 15px;
  bottom: 22px;
  width: 32px;
  height: 32px;
  z-index: 2;
}

.card-play-icon {
  position: absolute;
  left: 8px;
  bottom: 16px;
  width: 18px;
  height: 18px;
  z-index: 2;
}

/* 影游大卡观看进度条（章节粒度） */
.hero-progress {
  position: absolute;
  left: 15px;
  right: 15px;
  bottom: 10px;
  height: 3px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.35);
  overflow: hidden;
  z-index: 2;
}

.hero-progress-inner {
  height: 100%;
  border-radius: 20px;
  background: linear-gradient(270deg, rgba(255, 90, 205, 1) 0%, rgba(154, 87, 255, 1) 100%);
}

.play-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 89px;
  height: 32px;
  line-height: 32px;
  padding: 0 20px;
  border-radius: 30px;
  text-align: center;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  background: linear-gradient(270deg, rgba(255, 90, 205, 1) 0%, rgba(154, 87, 255, 1) 100%);
  cursor: pointer;

  .play-triangle {
    width: 0;
    height: 0;
    margin-right: 5px;
    border-style: solid;
    border-width: 5px 0 5px 8px;
    border-color: transparent transparent transparent #ffffff;
  }
}

/* 双页签 */
.shelf-tab {
  position: relative;
  padding-bottom: 8px;
  cursor: pointer;
  display: flex;
  align-items: baseline;

  span {
    font-size: 15px;
    color: var(--vp-shelf-tab, #7b7994);
  }

  .tab-count {
    margin-left: 4px;
    font-size: 12px;
  }

  &.active span {
    color: #ffffff;
    font-weight: bold;
    font-size: 17px;
  }

  .tab-underline {
    position: absolute;
    left: 0;
    bottom: 0;
    width: 23px;
    height: 4px;
    border-radius: 3px;
    background: linear-gradient(270deg, rgba(255, 90, 205, 1) 0%, rgba(154, 87, 255, 1) 100%);
  }
}

.tab-divider {
  width: 1px;
  height: 14px;
  background: rgba(255, 255, 255, 0.2);
}

/* 三列书格 */
.shelf-cover {
  aspect-ratio: 105 / 140;
  object-fit: cover;
}

.cover-progress {
  position: absolute;
  left: 8px;
  right: 8px;
  bottom: 8px;
  height: 3px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.35);
  overflow: hidden;
}

.cover-progress-inner {
  height: 100%;
  border-radius: 2px;
  background: linear-gradient(270deg, rgba(255, 107, 210, 1) 0%, rgba(161, 98, 255, 1) 100%);
}
</style>
